/**
 * A toolbar button's drop-down panel, shared by MultiSelect, DateFilter and
 * the audit log's source IP filter. The panel is positioned against the
 * viewport like RowMenu, so a toolbar cannot clip it; Escape, an outside
 * click, scrolling outside it or resizing closes it, and Escape returns focus
 * to the button. On open, the panel's first field takes focus.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export function usePopover() {
  const [position, setPosition] = useState<{ top: number; left: number; width: number } | null>(null);
  const button = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const open = position !== null;

  const close = useCallback((returnFocus = true) => {
    setPosition(null);
    if (returnFocus) button.current?.focus();
  }, []);

  function toggle() {
    if (open) return close();
    const rect = button.current!.getBoundingClientRect();
    // Keep a wide panel on screen when its button sits near the right edge.
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - 8 - (panel.current?.offsetWidth ?? 0)));
    setPosition({ top: rect.bottom + 6, left, width: rect.width });
  }

  useEffect(() => {
    if (!open) return;
    panel.current?.querySelector<HTMLElement>('input, button')?.focus();
    // Re-clamp once the panel has a width.
    const width = panel.current?.offsetWidth ?? 0;
    setPosition((p) => (p && p.left + width > window.innerWidth - 8 ? { ...p, left: Math.max(8, window.innerWidth - 8 - width) } : p));
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    const onPointer = (e: MouseEvent) => {
      if (!panel.current?.contains(e.target as Node) && !button.current?.contains(e.target as Node)) close(false);
    };
    // A resize's target is the window, not a Node: it always closes the popover.
    const onMove = (e: Event) => {
      if (!(e.target instanceof Node) || !panel.current?.contains(e.target)) close(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointer);
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointer);
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open, close]);

  const style = position
    ? // `right: auto` undoes .user-dropdown's `right: 0`, which would
      // otherwise stretch a fixed panel from its button to the window edge.
      ({ position: 'fixed', top: position.top, left: position.left, right: 'auto', minWidth: position.width } as const)
    : undefined;

  return { open, button, panel, toggle, close, style };
}
